/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * 34 Middletown Ave, Atlantic Highlands, NJ 07716
 *
 * THIS SOFTWARE IS THE CONFIDENTIAL AND PROPRIETARY INFORMATION OF
 * Aegis AO Soft LLC and Alexander Orlov.
 *
 * This code may be used, reproduced, modified, or distributed ONLY with the
 * prior written permission of Aegis AO Soft LLC / Alexander Orlov.
 *
 * Author: Alexander Orlov
 * Aegis AO Soft LLC
 */


import apiClient from './client';

/** One credential input a GPS provider needs (e.g. Fleet77 username/password). */
export interface GpsCredentialField {
  key: string;
  label: string;
  secret: boolean;
}

/** A GPS provider available to attach (loaded plugin). */
export interface GpsProviderDescriptor {
  providerId: string;
  providerName: string;
  /** Plugin-declared: 'credentials' (show fields) or 'oauth' (show a Connect button). */
  authKind?: string;
  credentialFields: GpsCredentialField[];
}

/** One of the owner's GPS accounts (credentials never echoed — only secretsPresent flags). */
export interface GpsAccountDto {
  /** An owner may hold several accounts of one provider, so every per-account call names it by this id. */
  id: string;
  providerId: string;
  nickname?: string | null;
  isActive: boolean;
  isConfigured: boolean;
  secretsPresent: Record<string, boolean>;
  lastScrapeAtUtc?: string | null;
  lastScrapeStatus?: string | null;
  consecutiveFailures: number;
}

export interface GpsPluginsResponse {
  providers: GpsProviderDescriptor[];
  /** All GPS accounts this owner has configured — several of one provider allowed; empty by default. */
  accounts: GpsAccountDto[];
}

export interface GpsSetConfigRequest {
  secrets: Record<string, string>;
  nickname?: string;
}

export interface GpsTestResponse {
  success: boolean;
  errorMessage?: string;
}

export interface GpsScanResponse {
  success: boolean;
  errorMessage?: string;
  vehicles?: number;
  points?: number;
  result?: {
    trackersUpserted: number;
    positionsInserted: number;
    positionsSkipped: number;
    vehiclesUnmatchedCar: number;
  };
}

export interface GpsVehicleSyncResponse {
  success: boolean;
  errorMessage?: string;
  result?: {
    total: number;
    created: number;
    assigned: number;
    alreadyAssigned: number;
    vinFixed: number;
    skippedOtherOwner: number;
    conflicts: number;
    aiMatched: number;
    pendingQueued: number;
  };
}

/** A GPS vendor vehicle the sync could not confidently match — awaiting owner review. */
export interface GpsPendingImport {
  id: string;
  providerId: string;
  imei?: string | null;
  vin?: string | null;
  plate?: string | null;
  plateState?: string | null;
  make?: string | null;
  model?: string | null;
  year?: number | null;
  title?: string | null;
  /** "unmatched" | "no-plate". */
  reason: string;
  createdAt: string;
}

/** One of the owner's device-less cars a pending vehicle can be linked to. */
export interface GpsLinkableCar {
  id: string;
  make?: string | null;
  model?: string | null;
  year?: number | null;
  plate?: string | null;
  vin?: string | null;
  title?: string | null;
}

/** What Tesla reports for an owner's cars (TeslaTelemetrySetupService.TeslaTelemetryStatus). */
export interface TeslaTelemetryStatus {
  vins: string[];
  keyPairedVins: string[];
  keyMissingVins: string[];
  configuredVehicles: number;
  /** Paired cars Tesla refused the config for, by reason (unsupported_firmware, max_configs, …). */
  skipped: Record<string, string[]>;
  /** tesla.com/_ak/<partner domain> — opened on a phone near the car to add our virtual key. */
  pairingUrl: string;
  problem?: string | null;
}

export interface GpsPendingImportsResponse {
  pending: GpsPendingImport[];
  linkableCars: GpsLinkableCar[];
}

/** Per-vehicle decision: link to an existing car, create a new one, or discard. */
export interface GpsResolvePendingRequest {
  action: 'link' | 'create' | 'discard';
  carId?: string;
}

/** `?accountId=` (and any extra params) for a per-account call; empty when there is nothing to add. */
export const accountQuery = (accountId?: string, extra: Record<string, string | number> = {}): string => {
  const q = new URLSearchParams();
  Object.entries(extra).forEach(([k, v]) => q.set(k, String(v)));
  if (accountId) q.set('accountId', accountId);
  const s = q.toString();
  return s ? `?${s}` : '';
};

/** Body of the 409 the API answers when the login is already connected for this owner. */
export interface GpsDuplicateLogin {
  message: string;
  existingAccountId: string;
  existingNickname?: string | null;
}

/** Per-owner GPS account management (mirrors ownerBookingPluginsApi against /owners/{id}/gps-plugins). */
export const gpsProviderApi = {
  async list(ownerId: string): Promise<GpsPluginsResponse> {
    const res = await apiClient.get<GpsPluginsResponse>(
      `/owners/${encodeURIComponent(ownerId)}/gps-plugins`);
    return res.data;
  },

  /**
   * Save an account. `accountId` updates that account; `asNew` attaches an ADDITIONAL account of the same
   * provider (a second login); neither updates the owner's only account of that provider.
   */
  async set(ownerId: string, providerId: string, body: GpsSetConfigRequest,
    opts: { accountId?: string; asNew?: boolean } = {}): Promise<void> {
    await apiClient.put(
      `/owners/${encodeURIComponent(ownerId)}/gps-plugins/${encodeURIComponent(providerId)}`
        + accountQuery(opts.accountId, opts.asNew ? { asNew: 'true' } : {}), body);
  },

  async delete(ownerId: string, providerId: string, accountId?: string): Promise<void> {
    await apiClient.delete(
      `/owners/${encodeURIComponent(ownerId)}/gps-plugins/${encodeURIComponent(providerId)}${accountQuery(accountId)}`);
  },

  async test(ownerId: string, providerId: string, accountId?: string): Promise<GpsTestResponse> {
    const res = await apiClient.post<GpsTestResponse>(
      `/owners/${encodeURIComponent(ownerId)}/gps-plugins/${encodeURIComponent(providerId)}/test${accountQuery(accountId)}`);
    return res.data;
  },

  async scan(ownerId: string, providerId: string, hoursBack = 48, accountId?: string): Promise<GpsScanResponse> {
    const res = await apiClient.post<GpsScanResponse>(
      `/owners/${encodeURIComponent(ownerId)}/gps-plugins/${encodeURIComponent(providerId)}/scan${accountQuery(accountId, { hoursBack })}`,
      undefined, { timeout: 120000 });
    return res.data;
  },

  async syncVehicles(ownerId: string, providerId: string, accountId?: string): Promise<GpsVehicleSyncResponse> {
    const res = await apiClient.post<GpsVehicleSyncResponse>(
      `/owners/${encodeURIComponent(ownerId)}/gps-plugins/${encodeURIComponent(providerId)}/sync-vehicles${accountQuery(accountId)}`,
      undefined, { timeout: 120000 });
    return res.data;
  },

  /**
   * Start a plugin's one-click OAuth connect: ask the backend for the vendor's authorize URL for this
   * provider, then send the browser there. Generic — driven by the provider's authKind, not hardcoded.
   */
  async oauthConnectUrl(providerId: string): Promise<string> {
    const res = await apiClient.get<{ url: string }>(`/gps/${encodeURIComponent(providerId)}/connect`);
    return res.data.url;
  },

  /**
   * Tesla: re-check which cars carry the MyEZToll virtual key and point those at our telemetry server.
   * Changes the cars' configuration, hence POST.
   */
  async teslaTelemetryRefresh(): Promise<TeslaTelemetryStatus> {
    const res = await apiClient.post<TeslaTelemetryStatus>('/gps/tesla/telemetry/refresh', {}, { timeout: 60000 });
    return res.data;
  },

  /** Admin: connect a Tesla for Business fleet to an owner with the code from our Consent Management page. */
  async teslaBusinessConnect(ownerId: string, authCode: string): Promise<{ connected: boolean; expiresAtUtc: string }> {
    const res = await apiClient.post<{ connected: boolean; expiresAtUtc: string }>(
      '/gps/tesla/business', { ownerId, authCode }, { timeout: 60000 });
    return res.data;
  },

  async getPendingImports(ownerId: string): Promise<GpsPendingImportsResponse> {
    const res = await apiClient.get<GpsPendingImportsResponse>(
      `/owners/${encodeURIComponent(ownerId)}/gps-plugins/pending-imports`);
    return res.data;
  },

  async resolvePendingImport(
    ownerId: string, id: string, body: GpsResolvePendingRequest): Promise<{ success: boolean; carId?: string }> {
    const res = await apiClient.post<{ success: boolean; carId?: string }>(
      `/owners/${encodeURIComponent(ownerId)}/gps-plugins/pending-imports/${encodeURIComponent(id)}/resolve`, body);
    return res.data;
  },
};
